/**
 * PeerBenchmarkTab — dedicated "Create Content · Peer benchmark" view.
 *
 * Enterprise-plan-only. Users on lower plans see a plain lock card with
 * an upgrade CTA. Users with `peer_benchmark` populated on their
 * `profile_scores` see the benchmark chart + a short interpretation.
 *
 * Data source: `data.profile_scores.peer_benchmark` (already returned by
 * `GET /api/analyze/{id}`). If missing, we show the "coming soon"
 * enterprise-lock state to avoid rendering an empty card.
 */
import React from 'react';
import { Lock, Users, Target, Compass, Sparkles } from 'lucide-react';
import { Card, CardContent } from './../ui/card';
import { Button } from './../ui/button';
import { Badge } from './../ui/badge';

export default function PeerBenchmarkTab({ data, onUpgrade, unlocked = false }) {
  const scoring = data?.profile_scores || {};
  const peer = scoring?.peer_benchmark || null;
  const cats = (scoring?.current || {}).categories || {};
  const potCats = (scoring?.potential || {}).categories || {};
  const peerCats = (peer?.categories) || {};
  // Feb-2026: positioning gap sits at the analysis-doc top level.
  const positioning = data?.peer_positioning || null;
  const rec = positioning?.recommended_positioning || null;
  const crowded = Array.isArray(positioning?.crowded_themes) ? positioning.crowded_themes : [];
  const underserved = Array.isArray(positioning?.underserved_niches) ? positioning.underserved_niches : [];

  return (
    <div className="space-y-6" data-testid="peer-benchmark-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono-edge text-[10px] tracking-[0.18em] text-primary">
            CREATE CONTENT · PEER BENCHMARK
          </p>
          <h2 className="text-2xl font-semibold mt-1">
            How you stack up against your peer cohort
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            De-identified median score for professionals in the same
            role, seniority, and industry as you. See where you&apos;re
            already ahead and where the cohort is pulling away.
          </p>
        </div>
      </div>

      {!unlocked && (
        <Card className="border-primary/25 bg-primary/[0.03]" data-testid="peer-benchmark-lock-card">
          <CardContent className="p-6 flex flex-col sm:flex-row gap-5 items-start sm:items-center">
            <div className="w-11 h-11 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
              <Lock size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold">Peer benchmark · Enterprise plan</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Peer benchmarking uses Blayzrr&apos;s de-identified cohort
                library — comparing your profile to real peers in your
                role, seniority, and industry. Available on the Enterprise
                plan. Contact your admin or upgrade to unlock.
              </p>
            </div>
            <Button
              onClick={onUpgrade}
              className="rounded-full shrink-0"
              data-testid="peer-benchmark-upgrade-btn"
            >
              <Users size={14} className="mr-2" /> Talk to sales
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Unlocked view — show the real peer benchmark side-by-side. */}
      {unlocked && peer && (
        <Card className="border-border" data-testid="peer-benchmark-unlocked-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <h3 className="text-sm font-mono-edge tracking-[0.14em] uppercase text-muted-foreground">
                {peer.label || 'Peer cohort'}
              </h3>
              {typeof peer.percentile_estimate === 'number' && (
                <span className="text-xs font-mono-edge px-2 py-1 rounded-full bg-primary/10 text-primary">
                  You&apos;re in the top {100 - peer.percentile_estimate}% of the cohort
                </span>
              )}
            </div>
            <div className="space-y-3">
              {Object.entries(cats).map(([k, v]) => {
                const potV = potCats[k];
                const peerV = peerCats[k];
                return (
                  <div key={k} className="grid grid-cols-[120px_1fr_auto] gap-3 items-center">
                    <div className="text-sm capitalize">{k.replace(/_/g, ' ')}</div>
                    <div className="relative h-2 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full bg-primary/70" style={{ width: `${Math.min(100, v)}%` }} />
                      {typeof peerV === 'number' && (
                        <div
                          className="absolute top-[-3px] w-0.5 h-3 bg-emerald-500"
                          style={{ left: `${Math.min(100, peerV)}%` }}
                          title={`Peer median: ${peerV}`}
                        />
                      )}
                    </div>
                    <div className="text-sm font-mono-edge text-muted-foreground">
                      {v}
                      {typeof potV === 'number' ? <span className="text-primary"> → {potV}</span> : null}
                      {typeof peerV === 'number' ? <span className="ml-2 text-emerald-600">· peer {peerV}</span> : null}
                    </div>
                  </div>
                );
              })}
            </div>
            {peer.notes ? (
              <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
                {peer.notes}
              </p>
            ) : null}
          </CardContent>
        </Card>
      )}

      {unlocked && !peer && (
        <Card className="border-dashed border-border" data-testid="peer-benchmark-empty-card">
          <CardContent className="p-6">
            <h3 className="text-base font-semibold">No peer data for this audit yet</h3>
            <p className="text-sm text-muted-foreground mt-1">
              This audit was created before peer benchmarking ran end-to-end. Re-run the audit
              to generate cohort comparisons, or contact your admin to backfill.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Positioning Gap panel (Feb-2026) — the actionable takeaway
          from the peer benchmark. Ships for every audit that has a
          peer_positioning block on the analysis doc; the block is
          generated during the audit's content-prep step (see
          `run_content_prep` -> `analyze_peer_positioning`). */}
      {positioning && (
        <Card
          className="border-primary/25 bg-gradient-to-br from-primary/[0.04] via-transparent to-transparent"
          data-testid="peer-positioning-card"
        >
          <CardContent className="p-6">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0">
                <Target size={18} strokeWidth={2.2} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-primary">
                  POSITIONING GAP
                </p>
                <h3 className="text-lg sm:text-xl font-semibold leading-tight mt-1">
                  Where your peers crowd — and the niche you can own instead.
                </h3>
                <p className="text-xs text-muted-foreground mt-1 max-w-2xl leading-relaxed">
                  Extracted from your curated peer cohort. Use this to steer
                  what you post about + how you rewrite your Headline.
                </p>
              </div>
              {positioning?.confidence && (
                <Badge
                  variant="secondary"
                  className="uppercase text-[10px] tracking-wider font-mono-edge shrink-0"
                  data-testid="peer-positioning-confidence"
                >
                  {positioning.confidence} confidence
                </Badge>
              )}
            </div>

            {rec?.unique_niche && (
              <div
                className="mt-5 rounded-2xl border border-primary/30 bg-primary/[0.06] p-4"
                data-testid="peer-positioning-recommended"
              >
                <div className="flex items-center gap-2">
                  <Sparkles size={13} className="text-primary" />
                  <span className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-primary">
                    Recommended positioning
                  </span>
                </div>
                <h4 className="text-base font-semibold mt-1.5 leading-snug">
                  {rec.unique_niche}
                </h4>
                {rec.one_line_promise && (
                  <p className="text-sm mt-1.5">
                    <span className="text-muted-foreground">Headline promise · </span>
                    <span className="font-medium">{rec.one_line_promise}</span>
                  </p>
                )}
                {rec.differentiation_summary && (
                  <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                    {rec.differentiation_summary}
                  </p>
                )}
                {Array.isArray(rec.supporting_evidence_from_profile) &&
                  rec.supporting_evidence_from_profile.length > 0 && (
                    <div className="mt-3">
                      <p className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground mb-1.5">
                        Grounded in your profile
                      </p>
                      <ul className="space-y-1">
                        {rec.supporting_evidence_from_profile.slice(0, 4).map((e, i) => (
                          <li
                            key={i}
                            className="text-xs text-muted-foreground pl-3 border-l-2 border-primary/30"
                          >
                            {e}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>
            )}

            <div className="mt-5 grid md:grid-cols-2 gap-4">
              {/* Crowded themes */}
              <div data-testid="peer-positioning-crowded">
                <p className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground mb-2">
                  Crowded themes (avoid replaying)
                </p>
                {crowded.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">
                    None extracted for this cohort.
                  </p>
                ) : (
                  <ul className="space-y-2.5">
                    {crowded.slice(0, 5).map((t, i) => (
                      <li
                        key={i}
                        className="rounded-xl border border-border bg-background p-3"
                        data-testid={`peer-positioning-crowded-${i}`}
                      >
                        <p className="text-sm font-medium leading-snug">
                          {t?.theme || '—'}
                        </p>
                        {t?.why_crowded && (
                          <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                            {t.why_crowded}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Underserved niches */}
              <div data-testid="peer-positioning-underserved">
                <p className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground mb-2">
                  White-space niches (lean into)
                </p>
                {underserved.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">
                    None extracted for this cohort.
                  </p>
                ) : (
                  <ul className="space-y-2.5">
                    {underserved.slice(0, 3).map((n, i) => (
                      <li
                        key={i}
                        className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.04] p-3"
                        data-testid={`peer-positioning-underserved-${i}`}
                      >
                        <div className="flex items-center gap-1.5">
                          <Compass size={11} className="text-emerald-600" />
                          <p className="text-sm font-medium leading-snug">
                            {n?.niche || '—'}
                          </p>
                        </div>
                        {n?.why_open && (
                          <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                            {n.why_open}
                          </p>
                        )}
                        {n?.positioning_angle && (
                          <p className="text-[11px] text-foreground mt-1.5 leading-relaxed">
                            <span className="text-muted-foreground">Angle · </span>
                            {n.positioning_angle}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <p className="text-[10.5px] text-muted-foreground mt-4">
              Data source · {positioning?.peer_data_source === 'scraped_context'
                ? 'Real scraped peer profiles (admin URL-led audit)'
                : positioning?.peer_data_source === 'curated_cohort'
                  ? 'Curated peer cohort themes'
                  : 'LLM estimate — enrich with scraped peer data on admin URL-led audits'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Sneak-peek: category bars are visible even when locked so users
          know what Enterprise unlocks. Only shown when the lock is in
          effect and we have a scorecard on the doc. */}
      {!unlocked && Object.keys(cats).length > 0 && (
        <Card className="border-border" data-testid="peer-benchmark-preview-card">
          <CardContent className="p-6">
            <h3 className="text-sm font-mono-edge tracking-[0.14em] uppercase text-muted-foreground">
              What Enterprise unlocks
            </h3>
            <p className="text-sm text-muted-foreground mt-2 mb-4">
              A cohort dot on every category showing where your peers
              sit — plus a &quot;top 10%&quot; benchmark to aim for.
            </p>
            <div className="space-y-3">
              {Object.entries(cats).map(([k, v]) => {
                const potV = potCats[k];
                return (
                  <div key={k} className="grid grid-cols-[120px_1fr_auto] gap-3 items-center">
                    <div className="text-sm capitalize">{k.replace(/_/g, ' ')}</div>
                    <div className="h-2 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full bg-primary/70" style={{ width: `${Math.min(100, v)}%` }} />
                    </div>
                    <div className="text-sm font-mono-edge text-muted-foreground">
                      {v}
                      {typeof potV === 'number' ? <span className="text-primary"> → {potV}</span> : null}
                      <span className="ml-2 opacity-40">· peer ?</span>
                    </div>
                  </div>
                );
              })}
            </div>
            {peer ? (
              <p className="text-xs text-muted-foreground mt-4">
                Peer data present on this audit — Enterprise view will
                render inline once the plan is active.
              </p>
            ) : null}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
